import React, { useContext, useState, useEffect, useRef } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  IconButton,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  capitalize,
  getItemBasePrice,
  getItemPrice,
  getVariantsBySize,
  isInventoryControlEnabled,
  isSalesTaxEnabled,
  isSalesTaxDefaultEnabled,
  pruneVariantFieldsOnSizeChange,
  isItemInStock,
  getOptimizedCloudinaryUrl,
  applyPhotoVariantPricing,
  syncItemImagesWithPhotoVariant,
  syncPhotoVariantWithItemImages,
} from "../../common/util";
import { STORAGE_BUCKET, IMG_PLACEHOLDER } from "../../common/constants";
import currency from "currency.js";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import bkstApi from "../../api";
import VariantSelection from "./VariantSelection";
import DoneIcon from "@mui/icons-material/Done";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import QuantityPicker from "../../common/component/QuantityPicker";
import ButtonWithConfirmation from "../../common/ButtonWithConfirmation";
import TotalSurchargeLabel from "../../common/component/lineitem/TotalSurchargeLabel";
import { UserContext } from "../../context/UserContext";
import WarningIcon from "@mui/icons-material/Warning";
import Images from "../../request/components/Images";
import { ExpandMoreOutlined } from "@mui/icons-material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";

export default function StandardItem(props) {
  const errorRef = useRef(null);
  const { user } = useContext(UserContext);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [product, setProduct] = useState(props.product || "");

  const [item, setItem] = useState(props.item || "");
  const [errors, setErrors] = useState([]);

  const [tab, setTab] = useState("options");
  const [open, setOpen] = useState(false);

  const loadProduct = (productId) => {
    bkstApi(`/my/product/${productId}`).then((res) => {
      setProduct(res.data);
    });
  };

  const areRequiredFieldsFilledOut = (variant, options) => {
    if (options) {
      for (const [key, value] of Object.entries(options)) {
        if (value.required && !variant?.[key]?.name) {
          return false;
        }
      }
    }

    return true;
  };

  const updatePriceAndTotal = (item) => {
    const price = getItemBasePrice(item.variant);
    const priceWithSurcharge = getItemPrice(item.variant, price);
    const total = currency(priceWithSurcharge).multiply(item.quantity).value;
    return { ...item, price, total };
  };

  useEffect(() => {
    if (!item?.variant || !product?.variants) {
      return;
    }
    const productKey = product.id ?? product.productId;
    if (item.productId && productKey && String(item.productId) !== String(productKey)) {
      return;
    }
    const fields = getVariantsBySize(product.variants, item);
    const synced = applyPhotoVariantPricing(item.variant, fields);
    setItem(updatePriceAndTotal({ ...item, variant: synced }));
  }, [item.variant, item.quantity, product]);

  const handleSubmit = (e, variantsWithOverride) => {
    e.preventDefault();
    e.stopPropagation();

    const arr = validate(item, variantsWithOverride);
    setErrors(arr);
    if (arr.length > 0) {
      errorRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    } else {
      const cartItem = isSalesTaxEnabled(user.config)
        ? {
            ...item,
            salesTax: {
              enabled:
                item?.salesTax?.enabled !== undefined
                  ? `${item.salesTax.enabled}` === "true"
                  : isSalesTaxDefaultEnabled(user.config),
            },
          }
        : item;
      props.onChange(cartItem);
      handleClose();
    }
  };

  const validate = (item, variantsWithOverride) => {
    let arr = [];
    if (!(+item.price > 0)) {
      arr.push("Price is required");
    }
    if (!(+item.quantity > 0)) {
      arr.push("Quantity is required");
    }
    if (item.type === "Other" && !item.title?.trim()) {
      arr.push("Title is required");
    }

    if (variantsWithOverride) {
      Object.entries(variantsWithOverride).map(([key, value]) => {
        if (value.required) {
          if (
            value.field.type === "select" &&
            value.field.options.length > 0 &&
            !item.variant?.[key]
          ) {
            arr.push(`${capitalize(key)} is required`);
          } else if (value.field.type === "text" && !item.variant?.[key]) {
            arr.push(`${capitalize(key)} is required`);
          }
        }
      });
    }

    return arr;
  };

  const handleDelete = () => {
    props.onChange("", "delete");
    handleClose();
  };

  const onBackButtonClick = () => {
    handleClose();
  };

  const handleClose = () => {
    setOpen(false);
    setErrors([]);
    setItem("");
    setProduct(props.product || "");
  };

  if (!open && props.item) {
    return (
      <span
        onClick={() => {
          setProduct("");
          loadProduct(props.item.productId);
          setItem(props.item);
          setOpen(true);
        }}
      >
        {props.children}
      </span>
    );
  } else if (!open && !props.item) {
    const fillGridCell = Boolean(props.fillGridCell);

    return (
      <Link
        color="inherit"
        underline="never"
        sx={{
          cursor: "pointer",
          textAlign: "center",
          ...(fillGridCell ? { display: "block", width: "100%" } : {}),
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          const p = props.product;
          setProduct(p);
          const productKey = p.id ?? p.productId;
          // Product config if set; otherwise bakery sales-tax default.
          const salesTaxEnabled =
            p.salesTax?.enabled !== undefined
              ? `${p.salesTax.enabled}` === "true"
              : isSalesTaxDefaultEnabled(user.config);
          let item = {
            productId: productKey,
            images: [p.images?.[0]],
            title: p.title,
            type: p.type,
            quantity: 1,
            salesTax: { enabled: salesTaxEnabled },
          };
          if (p?.variants?.size?.field?.options?.length === 1) {
            const defaultSize = p.variants.size.field.options[0];
            item.variant = { size: { ...defaultSize, value: defaultSize } };
          }

          if (isInventoryControlEnabled(user.acctId)) {
            loadProduct(productKey);
            setItem(item);
            setOpen(true);
          } else if (areRequiredFieldsFilledOut(item.variant, p.variants)) {
            // skip dialog and add directly
            props.onChange(updatePriceAndTotal(item));
          } else {
            setItem(item);
            setOpen(true);
          }
        }}
      >
        <Paper elevation={2} sx={{ borderRadius: 1, overflow: "hidden" }}>
          <Box
            sx={{
              width: fillGridCell ? "100%" : "160px",
              minWidth: fillGridCell ? 0 : undefined,
              maxWidth: fillGridCell ? "100%" : undefined,
              boxSizing: "border-box",
              minHeight: "75px",
            }}
          >
            <Box
              sx={{
                position: "relative",
                overflow: "hidden",
              }}
            >
              <img
                src={
                  product.images?.length > 0
                    ? getOptimizedCloudinaryUrl(product.images[0], "f_auto,q_auto,w_400")
                    : `${STORAGE_BUCKET}${IMG_PLACEHOLDER}`
                }
                alt={product.title || ""}
                style={{
                  width: "100%",
                  aspectRatio: "1",
                  objectFit: "cover",
                  display: "block",
                }}
              />
              {isInventoryControlEnabled(user.acctId) && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    backgroundColor: (+product.stock || 0) <= 0 ? "error.main" : "info.main",
                    color: "white",
                    padding: "4px 8px",
                    fontWeight: "bold",
                    fontSize: "12px",
                  }}
                >
                  {+product.stock || 0}
                </Box>
              )}
            </Box>
            <Box
              sx={{
                bgcolor: "grey.50",
                px: 1.5,
                py: 1,
                minHeight: 44,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography
                color="textSecondary"
                variant="body2"
                sx={{
                  fontSize: "0.8125rem",
                  lineHeight: 1.35,
                  textTransform: "none",
                  textAlign: "center",
                  width: "100%",
                  maxWidth: "100%",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
              >
                {product.title}
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Link>
    );
  } else if (open && product) {
    let tmp = getVariantsBySize(product.variants, item);

    const optionFields = Object.entries(tmp).filter(
      ([k, v]) => v.field.type === "select" || v.field.type === "photo"
    );
    const textFields = Object.entries(tmp).filter(([k, v]) => {
      if (v.field.type === "text") {
        return true;
      } else {
        return false;
      }
    });

    return (
      <Dialog fullScreen={true} open={open} scroll="paper" onClose={handleClose}>
        <form
          onSubmit={(e) => handleSubmit(e, tmp)}
          style={{
            display: "contents",
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              gap: "10px",
              justifyContent: "flex-start",
              alignItems: "center",
              margin: "0",
              backgroundColor: "#F7F7F7",
              borderBottom: "1px solid #DDD",
              flexWrap: "wrap",
              padding: isMobile ? "8px" : "16px 24px 8px",
            }}
          >
            <IconButton onClick={onBackButtonClick}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="h5">
                {item.title}
                {isInventoryControlEnabled(user.acctId) && isItemInStock(user.acctId, product) && (
                  <Chip size="small" color="info" label={`${product.stock} left`} />
                )}
                {isInventoryControlEnabled(user.acctId) && !isItemInStock(user.acctId, product) && (
                  <Chip size="small" color="error" label={`out of stock`} />
                )}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "right",
                flexGrow: "1",
                gap: "5px",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box sx={{ fontSize: "1.4rem", fontWeight: "700" }}>
                  {currency(item.price).format()}
                </Box>
                <TotalSurchargeLabel item={item} />
              </Box>
              <QuantityPicker
                value={item.quantity}
                onChange={(val) => {
                  setItem({ ...item, quantity: val });
                }}
              />
              <Button
                type="submit"
                color="primary"
                variant="contained"
                sx={{ height: "56px", whiteSpace: "nowrap", width: "10rem", maxWidth: "12rem" }}
                endIcon={<DoneIcon />}
                disabled={+(item?.total || "") <= 0}
              >
                {currency(item.total).format()}
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ backgroundColor: "#F7F7F7", padding: "0" }}>
            <Box ref={errorRef}>
              {errors.length > 0 && (
                <Alert sx={{ justifyContent: "center" }} severity="error">
                  {errors.join(". ")}
                </Alert>
              )}
              {isInventoryControlEnabled(user.acctId) && (+product.stock || 0) < item.quantity && (
                <Box>
                  <Alert
                    sx={{ justifyContent: "center", fontSize: "1rem" }}
                    severity="warning"
                    variant="filled"
                    icon={<WarningIcon />}
                  >
                    The selected quantity ({item.quantity}) is greater than the current stock (
                    {+product.stock || 0}).
                  </Alert>
                </Box>
              )}
            </Box>
            <Box px={2} py={2}>
              {item && (
                <Box>
                  <Grid container>
                    <Grid item sm={4} xs={4}>
                      <Box pr={4} sx={{ position: "-webkit-sticky", position: "sticky", top: "0" }}>
                        <Images
                          edit={true}
                          pics={item.images}
                          onChange={(images) => {
                            const nextVariant = syncPhotoVariantWithItemImages(
                              images,
                              item.images,
                              item.variant,
                              tmp,
                              product.images
                            );
                            setItem({
                              ...item,
                              variant: nextVariant,
                              images: syncItemImagesWithPhotoVariant(
                                images,
                                item.variant,
                                nextVariant,
                                tmp
                              ),
                            });
                          }}
                          folder={`tmp`}
                        />
                      </Box>
                    </Grid>
                    <Grid item sm={8} sx={12}>
                      {textFields?.length > 0 && (
                        <Box>
                          <Tabs
                            value={tab}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                          >
                            <Tab
                              value="options"
                              label={"Options"}
                              onClick={() => setTab("options")}
                            />
                            <Tab value="notes" label={"Notes"} onClick={() => setTab("notes")} />
                          </Tabs>
                        </Box>
                      )}
                      {tab === "options" && (
                        <Box>
                          <VariantSelection
                            variant={item.variant || ""}
                            options={optionFields}
                            onChange={(variant) => {
                              const pruned = pruneVariantFieldsOnSizeChange(
                                variant,
                                item.variant || {},
                                tmp
                              );
                              const synced = applyPhotoVariantPricing(pruned, tmp);
                              setItem({
                                ...item,
                                variant: synced,
                                images: syncItemImagesWithPhotoVariant(
                                  item.images,
                                  item.variant,
                                  synced,
                                  tmp
                                ),
                              });
                            }}
                          />
                          {isSalesTaxEnabled(user.config) && (
                            <Box sx={{ marginTop: "15px", marginBottom: "15px" }}>
                              <Accordion elevation={0} defaultExpanded={false}>
                                <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                                  <Box
                                    style={{ display: "flex", alignItems: "center", gap: "7px" }}
                                  >
                                    <SettingsOutlinedIcon
                                      style={{ fontSize: "1.3rem", color: "#777" }}
                                    />
                                    <Typography variant="subtitle1">Settings</Typography>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                    Override product catalog tax setting for this cart line only.
                                  </Typography>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={
                                          item?.salesTax?.enabled !== undefined
                                            ? `${item.salesTax.enabled}` === "true"
                                            : isSalesTaxDefaultEnabled(user.config)
                                        }
                                        onChange={(e) =>
                                          setItem({
                                            ...item,
                                            salesTax: { enabled: e.target.checked },
                                          })
                                        }
                                      />
                                    }
                                    label="Apply sales tax to this item"
                                  />
                                </AccordionDetails>
                              </Accordion>
                            </Box>
                          )}
                          <Box my={2}>
                            {textFields && textFields.length > 0 ? (
                              <Button
                                size="large"
                                fullWidth
                                variant="contained"
                                color="secondary"
                                onClick={() => setTab("notes")}
                              >
                                CONTINUE
                              </Button>
                            ) : (
                              <Button
                                size="large"
                                fullWidth
                                variant="contained"
                                color="secondary"
                                onClick={(e) => handleSubmit(e, tmp)}
                              >
                                SAVE
                              </Button>
                            )}
                          </Box>
                        </Box>
                      )}
                      {tab === "notes" && (
                        <Box>
                          <Paper elevation={0}>
                            <Box px={2} py={2}>
                              <VariantSelection
                                variant={item.variant || ""}
                                options={textFields}
                                onChange={(variant) => {
                                  setItem({ ...item, variant });
                                }}
                              />
                            </Box>
                          </Paper>
                          <Box my={2}>
                            <Button
                              size="large"
                              fullWidth
                              variant="contained"
                              color="secondary"
                              onClick={(e) => handleSubmit(e, tmp)}
                            >
                              SAVE
                            </Button>
                          </Box>
                        </Box>
                      )}
                      {props.item && (
                        <Box my={4} textAlign={"center"}>
                          <ButtonWithConfirmation
                            copy="Are you sure you want to delete this item?"
                            onConfirm={handleDelete}
                          >
                            <Button color="error">Delete This Item</Button>
                          </ButtonWithConfirmation>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </DialogContent>
        </form>
      </Dialog>
    );
  }
}
